import { useState, useEffect, useRef } from "react";
import { Modal } from "../common/Modal";
import { InlineAISetup } from "../ai/InlineAISetup";
import styles from "./ProjectSettingsModal.module.css";

interface ProjectSettingsModalProps {
  title: string;
  createdAt: string;
  updatedAt: string;
  saving: boolean;
  deleting: boolean;
  aiConfigured: boolean | null;
  aiEnabled: boolean;
  aiNeedsVerification?: boolean;
  onSave: (newTitle: string) => void;
  onDelete: () => void;
  onClose: () => void;
  onConfigured: () => void;
  onNavigateToAISettings: () => void;
}

export function ProjectSettingsModal({
  title,
  createdAt,
  updatedAt,
  saving,
  deleting,
  aiConfigured,
  aiEnabled,
  aiNeedsVerification = false,
  onSave,
  onDelete,
  onClose,
  onConfigured,
  onNavigateToAISettings,
}: ProjectSettingsModalProps) {
  const [editedTitle, setEditedTitle] = useState(title);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [activeTab, setActiveTab] = useState<"general" | "ai">("general");
  const inputRef = useRef<HTMLInputElement>(null);

  const titleChanged = editedTitle.trim() !== "" && editedTitle.trim() !== title;
  const busy = saving || deleting;
  // Only show the "!" badge for actionable issues:
  // - AI is configured but the user disabled it (they should know)
  // - AI config exists but hasn't been verified yet
  // Don't badge when AI simply isn't set up — that's not urgent.
  const showAiBadge = (aiConfigured === true && !aiEnabled) || aiNeedsVerification;

  useEffect(() => {
    setEditedTitle(title);
  }, [title]);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleClose = () => {
    if (!busy) {
      if (confirmDelete) {
        setConfirmDelete(false);
      } else {
        onClose();
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (titleChanged && !busy) {
      onSave(editedTitle.trim());
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <Modal
      onClose={handleClose}
      preventClose={busy}
      labelledBy="project-settings-title"
      className={styles.modal}
    >
      <div className={styles.header}>
        <h2 id="project-settings-title" className={styles.title}>
          Project Settings
        </h2>
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close"
          disabled={busy}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === "general" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("general")}
        >
          General
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === "ai" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("ai")}
        >
          AI
          {showAiBadge && <span className={styles.tabBadge}>!</span>}
        </button>
      </div>

      {activeTab === "general" && (
        <>
          <form onSubmit={handleSubmit}>
            <div className={styles.section}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="project-title">
                  Project name
                </label>
                <input
                  ref={inputRef}
                  id="project-title"
                  type="text"
                  className={styles.input}
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  disabled={busy}
                  maxLength={200}
                />
              </div>
              <button type="submit" className={styles.saveButton} disabled={!titleChanged || busy}>
                {saving ? "Saving…" : "Save"}
              </button>
              <div className={styles.meta}>
                Created {formatDate(createdAt)} · Last updated {formatDate(updatedAt)}
              </div>
            </div>
          </form>

          <div className={styles.dangerZone}>
            <h3 className={styles.dangerTitle}>Danger zone</h3>
            <p className={styles.dangerDescription}>
              Permanently delete this project and all its content — stream notes, outline, and
              draft. This cannot be undone.
            </p>
            {!confirmDelete ? (
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
              >
                Delete project
              </button>
            ) : (
              <button
                type="button"
                className={styles.deleteButton}
                onClick={onDelete}
                disabled={busy}
              >
                {deleting ? "Deleting…" : "Confirm — delete forever"}
              </button>
            )}
          </div>
        </>
      )}

      {activeTab === "ai" && (
        <div className={styles.section}>
          <p className={styles.aiDescription}>
            AI features help generate outlines, drafts, and suggestions for your project.
          </p>

          {aiConfigured === true && (
            <>
              {aiEnabled ? (
                <div className={styles.aiStatusBanner}>
                  <span className={styles.aiStatusDot} />
                  <span className={styles.aiStatusText}>AI is configured and active</span>
                </div>
              ) : (
                <div className={`${styles.aiStatusBanner} ${styles.aiStatusBannerWarning}`}>
                  <span className={`${styles.aiStatusDot} ${styles.aiStatusDotWarning}`} />
                  <span className={styles.aiStatusText}>AI is configured but disabled</span>
                </div>
              )}
              <button
                type="button"
                className={styles.saveButton}
                onClick={() => {
                  onClose();
                  onNavigateToAISettings();
                }}
              >
                Manage AI Settings
              </button>
            </>
          )}

          {aiConfigured !== true && (
            <InlineAISetup
              onConfigured={() => {
                onConfigured();
                onClose();
              }}
            />
          )}
        </div>
      )}
    </Modal>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 4L12 12M12 4L4 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
